import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Accordion, AccordionDetails, AccordionSummary, Box, Grid, Link, Typography } from '@mui/material';
import { DataGrid, GridEventListener } from '@mui/x-data-grid';
import {
  camelCase,
  castArray,
  every,
  filter,
  first,
  includes,
  isArray,
  isEmpty,
  isString,
  map,
  size,
  some,
  uniq,
  upperFirst,
} from 'lodash';
import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { StringParam, useQueryParam } from 'use-query-params';

import JobResultsChartList from 'components/FeaturesDashboard/JobResultsChartList';
import ReportJobSectionNestedControls from 'components/FeaturesDashboard/JobResultsChartList/ReportJobSectionNestedControls';
import Loader, { LoadingStage } from 'components/Loader';
import { useJobColumns } from 'components/Pages/Jobs/columns';
import { buildStudyReportSectionNavigation } from 'components/Pages/StudyReport/StudyReportSectionNavigation';
import StudyReportSectionsLayout from 'components/Pages/StudyReport/StudyReportSectionsLayout';
import StudyReportSectionSurface from 'components/Pages/StudyReport/StudyReportSectionSurface';
import { getRenderableReportJobResults, Job, JobArchiveState, JobStatus, JobType } from 'interfaces/job';
import { formatDateTimeForUser } from 'utils/dateTime';
import { JobResultSlideLabelModeProvider } from 'utils/jobResultAugmentation';
import { useJob } from 'utils/queryHooks/jobs/useJob';
import { useJobs } from 'utils/queryHooks/jobs/useJobs';
import {
  buildReportJobDisplaySections,
  ReportJobNestedSectionCombineMode,
  ReportJobNestedSectionLayoutMode,
} from 'utils/reportJobSections';
import { useCurrentLabId } from 'utils/useCurrentLab';
import { useNavigationToViewerPage } from 'utils/useNavigationToViewerPage';
import { getDefaultReportTabJobId, reportTabJobStatuses } from './reportTabJobs';

interface Props<JobInterface extends Job> {
  tabTitle: string;
  jobType?: JobType;
  jobTypes?: JobType[];
  pageSize?: number;
  selectedStudyId?: string;
  currentJobIdQueryParamName?: string;
  emptyStateMessage?: string;
  renderSelectedJob?: (params: { job: JobInterface; jobManifest: string[]; labId: string }) => React.ReactNode;
}

const displayedColumns = ['startedAt', 'status', 'name', 'requestSender', 'userId'];
const REPORT_CHART_GRID_HEIGHT = 600;
const REPORT_TAB_REFETCH_INTERVAL_MS = 30000;

interface SelectedReportJobPanelProps<JobInterface extends Job> {
  job: JobInterface;
  fallbackStudyId?: string;
  labId: string;
  tabTitle: string;
  emptyStateMessage: string;
  renderSelectedJob?: (params: { job: JobInterface; jobManifest: string[]; labId: string }) => React.ReactNode;
}

const isReportJobManifest = (manifest: unknown): manifest is string[] =>
  isArray(manifest) && every(manifest, (item) => isString(item));

const SelectedReportJobPanel = React.memo(function SelectedReportJobPanel<JobInterface extends Job>({
  job,
  fallbackStudyId,
  labId,
  tabTitle,
  emptyStateMessage,
  renderSelectedJob,
}: SelectedReportJobPanelProps<JobInterface>) {
  const { getUrlToSlidePage } = useNavigationToViewerPage();
  const jobManifest = isReportJobManifest(job.manifest) ? job.manifest : [];
  const selectedJobStudyId = job.studyId || fallbackStudyId || '';
  const jobResultsValue = (job as any).results;
  const jobResults = getRenderableReportJobResults(jobResultsValue);
  const [nestedSectionCombineModeByGroupId, setNestedSectionCombineModeByGroupId] = React.useState<
    Record<string, ReportJobNestedSectionCombineMode>
  >({});
  const [nestedSectionLayoutModeByGroupId, setNestedSectionLayoutModeByGroupId] = React.useState<
    Record<string, ReportJobNestedSectionLayoutMode>
  >({});
  const reportDisplaySections = React.useMemo(
    () =>
      buildReportJobDisplaySections(jobResults, {
        nestedSectionCombineModeByGroupId,
        nestedSectionLayoutModeByGroupId,
      }),
    [jobResults, nestedSectionCombineModeByGroupId, nestedSectionLayoutModeByGroupId]
  );
  const reportSectionNavigation = React.useMemo(
    () =>
      buildStudyReportSectionNavigation(
        reportDisplaySections.map((section) => ({
          id: section.id,
          title: section.title,
          navParentId: section.navigationParentId,
          navParentTitle: section.navigationParentTitle,
        }))
      ),
    [reportDisplaySections]
  );
  const reportSectionNumberLabelById = React.useMemo(
    () => new Map(reportSectionNavigation.resolvedSections.map((section) => [section.id, section.numberLabel])),
    [reportSectionNavigation]
  );
  const reportSectionParentNumberLabelById = React.useMemo(() => {
    const numberLabelByParentId = new Map<string, string>();

    reportDisplaySections.forEach((section) => {
      if (!section.navigationParentId) {
        return;
      }

      const childNumberLabel = reportSectionNumberLabelById.get(section.id);
      const parentNumberLabel = childNumberLabel?.split('.').slice(0, -1).join('.');

      if (parentNumberLabel && !numberLabelByParentId.has(section.navigationParentId)) {
        numberLabelByParentId.set(section.navigationParentId, parentNumberLabel);
      }
    });

    return numberLabelByParentId;
  }, [reportDisplaySections, reportSectionNumberLabelById]);

  const jobSummaryContent = (
    <Accordion>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Grid item>
          <Typography variant="h3">
            {job.name} from {size(job.manifest)} slides - {formatDateTimeForUser(job.startedAt) || 'Unknown run date'}
          </Typography>
          {job.id && (
            <Typography variant="caption" display="block">
              {job.id}
            </Typography>
          )}
          <Typography variant="caption">Click to view slides</Typography>
        </Grid>
      </AccordionSummary>
      <AccordionDetails>
        {map(uniq(jobManifest), (slideId) => (
          <Grid item key={slideId}>
            <Link
              to={getUrlToSlidePage({ slideId, labId, caseStudyId: selectedJobStudyId })}
              target="_blank"
              rel="noopener noreferrer"
              component={RouterLink}
            >
              <Typography variant="caption" key={slideId}>
                {slideId}
              </Typography>
            </Link>
          </Grid>
        ))}
      </AccordionDetails>
    </Accordion>
  );

  const reportSections = reportDisplaySections.map((section, index) => {
    const nestedSectionControl = section.nestedSectionControl;

    return {
      id: section.id,
      title: section.title,
      numberLabel: reportSectionNumberLabelById.get(section.id) || `${index + 1}`,
      navParentId: section.navigationParentId,
      navParentTitle: section.navigationParentTitle,
      rowGroupId: section.rowGroupId,
      rowColumns: section.rowColumns,
      rowGroupTitle: section.navigationParentTitle,
      rowGroupNumberLabel: section.rowGroupId ? reportSectionParentNumberLabelById.get(section.rowGroupId) : undefined,
      rowGroupActions:
        section.rowGroupId && nestedSectionControl ? (
          <ReportJobSectionNestedControls
            combineMode={nestedSectionControl.combineMode}
            layoutMode={nestedSectionControl.layoutMode}
            onCombineModeChange={(nextCombineMode) => {
              setNestedSectionCombineModeByGroupId((currentValue) => ({
                ...currentValue,
                [nestedSectionControl.groupId]: nextCombineMode,
              }));
            }}
            onLayoutModeChange={(nextLayoutMode) => {
              setNestedSectionLayoutModeByGroupId((currentValue) => ({
                ...currentValue,
                [nestedSectionControl.groupId]: nextLayoutMode,
              }));
            }}
          />
        ) : undefined,
      content: (
        <StudyReportSectionSurface
          sectionNumber={reportSectionNumberLabelById.get(section.id) || `${index + 1}`}
          title={section.title}
        >
          {nestedSectionControl && !section.rowGroupId ? (
            <Box sx={{ mb: 2 }}>
              <ReportJobSectionNestedControls
                combineMode={nestedSectionControl.combineMode}
                layoutMode={nestedSectionControl.layoutMode}
                onCombineModeChange={(nextCombineMode) => {
                  setNestedSectionCombineModeByGroupId((currentValue) => ({
                    ...currentValue,
                    [nestedSectionControl.groupId]: nextCombineMode,
                  }));
                }}
                onLayoutModeChange={(nextLayoutMode) => {
                  setNestedSectionLayoutModeByGroupId((currentValue) => ({
                    ...currentValue,
                    [nestedSectionControl.groupId]: nextLayoutMode,
                  }));
                }}
              />
            </Box>
          ) : null}
          <Box sx={{ minHeight: REPORT_CHART_GRID_HEIGHT }}>
            <JobResultsChartList studyId={selectedJobStudyId} jobId={job.id || ''} resultGroup={section.resultGroup} />
          </Box>
        </StudyReportSectionSurface>
      ),
    };
  });

  const defaultSelectedJobContent = (
    <StudyReportSectionsLayout
      reportTitle={job.name || tabTitle}
      scrollSpyAnchor={jobSummaryContent}
      sections={reportSections}
      emptyState={
        <Grid item>
          <Typography variant="body2" color="text.secondary">
            {emptyStateMessage}
          </Typography>
        </Grid>
      }
      sectionsContainerSx={{ px: 2, pb: 2 }}
    />
  );

  return (
    <JobResultSlideLabelModeProvider>
      {renderSelectedJob ? (
        <Grid item container direction="column" spacing={1} wrap="nowrap" height="100%">
          <Grid item>{jobSummaryContent}</Grid>
          {renderSelectedJob({ job, jobManifest, labId })}
        </Grid>
      ) : (
        defaultSelectedJobContent
      )}
    </JobResultSlideLabelModeProvider>
  );
}) as <JobInterface extends Job>(props: SelectedReportJobPanelProps<JobInterface>) => React.ReactElement;

const JobReportTab = <JobInterface extends Job>({
  tabTitle,
  jobType,
  jobTypes,
  pageSize = 10,
  selectedStudyId,
  currentJobIdQueryParamName,
  emptyStateMessage,
  renderSelectedJob,
}: React.PropsWithChildren<Props<JobInterface>>) => {
  const { labId } = useCurrentLabId();
  const selectedJobTypes = size(jobTypes) ? jobTypes : jobType ? [jobType] : [];

  const [page, setPage] = React.useState(1);
  const [shouldPollJobs, setShouldPollJobs] = React.useState(true);
  const {
    data: jobsResponse,
    isFetching: isFetchingJobs,
    isLoading: isLoadingJobs,
  } = useJobs<JobInterface>({
    page,
    pageSize,
    enabled: true,
    fetchWithInterval: shouldPollJobs,
    refetchInterval: REPORT_TAB_REFETCH_INTERVAL_MS,
    studyId: selectedStudyId,
    additionalFilters: {
      archiveState: JobArchiveState.Active,
      types: selectedJobTypes,
      statuses: [...reportTabJobStatuses],
    },
  });

  const jobsForTable = jobsResponse?.jobs || [];
  React.useEffect(() => {
    if (isFetchingJobs) {
      return;
    }

    const hasMutableJobs = isEmpty(jobsForTable) || some(jobsForTable, ({ status }) => status !== JobStatus.Completed);

    if (shouldPollJobs !== hasMutableJobs) {
      setShouldPollJobs(hasMutableJobs);
    }
  }, [isFetchingJobs, jobsForTable, shouldPollJobs]);

  const jobsCount = jobsResponse?.totalJobs ?? size(jobsForTable);

  const { jobColumns, isLoading: isLoadingColumnsQueries } = useJobColumns({
    viewOnly: true,
    usersById: jobsResponse?.usersById,
  });

  const [currentJobIdFromUrl, setCurrentJobId] = useQueryParam(
    currentJobIdQueryParamName ||
      `jobIdFor${upperFirst(camelCase(jobType || first(selectedJobTypes) || tabTitle))}ReportTab`,
    StringParam
  );

  const currentJobId = getDefaultReportTabJobId(jobsForTable, currentJobIdFromUrl);
  const resolvedEmptyStateMessage = emptyStateMessage || `No ${tabTitle} results are available yet.`;

  React.useEffect(() => {
    if (!currentJobId || currentJobIdFromUrl === currentJobId) {
      return;
    }

    setCurrentJobId(currentJobId);
  }, [currentJobId, currentJobIdFromUrl, setCurrentJobId]);

  const handleRowClick: GridEventListener<'rowClick'> = (params) => {
    setCurrentJobId(params.row.id);
  };

  const jobLoadingEnabled = !isEmpty(currentJobId);
  const { data: job, isLoading: isLoadingJobState } = useJob<JobInterface>(currentJobId, {
    enabled: jobLoadingEnabled,
    retry: true,
  });
  const isLoadingJob = jobLoadingEnabled && isLoadingJobState;

  return (
    <Grid item container spacing={2} pt={4} paddingInline={9} xs={12} direction="column" wrap="nowrap" height="100%">
      <Grid item xs="auto">
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Grid item>
              <Typography variant="h2">{tabTitle}</Typography>
              <Typography variant="caption">
                {isLoadingColumnsQueries || isLoadingJobs
                  ? 'Loading...'
                  : isEmpty(jobsForTable)
                  ? resolvedEmptyStateMessage
                  : 'Open to select a job'}
              </Typography>
            </Grid>
          </AccordionSummary>
          <AccordionDetails>
            <Box sx={{ width: 1000, height: 320 }}>
              <DataGrid
                paginationModel={{ page: page - 1, pageSize }}
                onPaginationModelChange={({ page: newPage }) => setPage(newPage + 1)}
                loading={isLoadingColumnsQueries || isLoadingJobs}
                pagination
                rows={jobsForTable}
                rowCount={jobsCount}
                columns={[
                  ...filter(jobColumns, (column) => includes(displayedColumns, column.field)),
                  { field: 'manifest', headerName: 'Manifest', flex: 1 },
                ]}
                onRowClick={handleRowClick}
                paginationMode="server"
                pageSizeOptions={castArray(pageSize)}
              />
            </Box>
          </AccordionDetails>
        </Accordion>
      </Grid>
      {isLoadingJobs || isLoadingJob ? (
        <Grid item>
          <Loader stage={LoadingStage.LoadingReport} />
        </Grid>
      ) : !job && !currentJobId ? (
        <Grid item>
          <Typography variant="body2" color="text.secondary">
            {resolvedEmptyStateMessage}
          </Typography>
        </Grid>
      ) : !job && currentJobId ? (
        <Grid item>
          <Typography variant="h3">
            {`No ${tabTitle} job found with ID: ${currentJobId}. Please select a different job from the list.`}
          </Typography>
        </Grid>
      ) : job ? (
        <SelectedReportJobPanel
          job={job}
          fallbackStudyId={selectedStudyId}
          labId={labId}
          tabTitle={tabTitle}
          emptyStateMessage={resolvedEmptyStateMessage}
          renderSelectedJob={renderSelectedJob}
        />
      ) : null}
    </Grid>
  );
};

export default JobReportTab;
